"use client";
import {
  createContext,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
  useEffect,
} from "react";
import { FundWithTranches } from "@/model/db";

type FundTranche = FundWithTranches["tranches"][0] | null;

interface FundsContextType {
  // Data
  allFunds: FundWithTranches[];
  selectedFund: FundWithTranches | null;
  selectedTranche: FundTranche | null;

  error: boolean;
  loading: boolean;

  // Actions
  setSelectedFund: (fundId: number) => void;
  setSelectedTranche: (trancheId: number) => void;
}

// Create context
const FundsContext = createContext<FundsContextType | undefined>(undefined);

// Custom hook to use the context
export function useFunds(): FundsContextType {
  const context = useContext(FundsContext);
  if (!context) {
    throw new Error("useFunds must be used within a FundsProvider");
  }
  return context;
}

// Provider component props
interface FundsProviderProps {
  children: ReactNode;
  initialFunds: FundWithTranches[];
  initialFundId?: number;
}

// Provider component
export function FundsProvider({
  children,
  initialFunds,
  initialFundId,
}: FundsProviderProps): ReactElement {
  const [allFunds] = useState<FundWithTranches[]>(initialFunds);
  const [selectedFund, setSelectedFundState] =
    useState<FundWithTranches | null>(null);
  const [selectedTranche, setSelectedTrancheState] =
    useState<FundTranche | null>(null);

  useEffect(() => {
    const startId = initialFundId ?? initialFunds[0]?.id;
    if (startId !== undefined) {
      setSelectedFund(startId);
    }
  }, []);

  function setSelectedFund(fundId: number): void {
    const fund = allFunds.find((f) => f.id === fundId);

    if (fund) {
      setSelectedFundState(fund);
      if (fund.tranches.length > 0) {
        setSelectedTrancheState(fund.tranches[0]);
      }
    }
  }

  // Set selected tranche
  function setSelectedTranche(trancheId: number): void {
    const tranche =
      selectedFund?.tranches?.find((t) => t.id === trancheId) || null;

    if (tranche) {
      setSelectedTrancheState(tranche);
    }
  }

  const contextValue: FundsContextType = {
    // Data
    allFunds,
    selectedFund,
    selectedTranche,

    error: !allFunds.length,
    loading: !selectedFund,

    // Actions
    setSelectedFund,
    setSelectedTranche,
  };

  return (
    <FundsContext.Provider value={contextValue}>
      {children}
    </FundsContext.Provider>
  );
}
